import React, { useState } from "react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { toast } from "sonner";
import PhotoUpload from "./PhotoUpload";
import { MagnifyingGlass, WarningOctagon, CheckCircle, CaretDown } from "@phosphor-icons/react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

export default function DumperInspectionDialog({ open, onOpenChange, onDone, siteId }) {
  const { meta } = useAuth();
  const [dumper, setDumper] = useState("");
  const [engineHours, setEngineHours] = useState("");
  const [vehicleKm, setVehicleKm] = useState("");
  const [performedDate, setPerformedDate] = useState(todayStr());
  const [tyres, setTyres] = useState(null);
  const [findings, setFindings] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [addMore, setAddMore] = useState(false);
  const [onboardRows, setOnboardRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [truckAxles, setTruckAxles] = useState("3");
  const [matches, setMatches] = useState(null);
  const [pickedSize, setPickedSize] = useState(null);
  const [pickedSite, setPickedSite] = useState(null);

  const reset = () => { setDumper(""); setEngineHours(""); setPerformedDate(todayStr()); setTyres(null); setFindings({}); setNotFound(false); setAddMore(false); setOnboardRows([]); setTruckAxles("3"); setMatches(null); setPickedSize(null); setPickedSite(null); setVehicleKm(""); };

  const loadTyres = async (sizeArg, siteArg) => {
    if (!dumper.trim()) { toast.error("Enter a dumper number"); return; }
    const sz = sizeArg;
    const st = siteArg;
    setLoading(true);
    setNotFound(false);
    try {
      const params = new URLSearchParams({ dumper_number: dumper.trim() });
      if (st) params.set("mine_site_id", st);
      if (sz) params.set("size", sz);
      const { data } = await api.get(`/dumper-tyres?${params.toString()}`);
      if (data.ambiguous) { setMatches(data.matches); setTyres(null); setLoading(false); return; }
      setMatches(null); setPickedSize(data.size); setPickedSite(data.mine_site_id);
      setTyres(data.tyres);
      const init = {};
      data.tyres.forEach((t) => { init[t.tyre_id] = { problem: false }; });
      setFindings(init);
      if (data.tyres.length === 0) toast.info("No fitted tyres on this dumper");
    } catch (e) {
      if (e.response?.status === 404) {
        setTyres(null); setMatches(null); setNotFound(true);
        setOnboardRows([{ uid: uid(), serial_number: "", size: (meta?.tyre_sizes || [])[1] || "27.00.49", position: "", tread_depth: "", condition: "Existing" }]);
        toast.info("New dumper — add its existing tyres, then inspect");
      } else {
        setTyres(null); setMatches(null);
        toast.error(`Could not load dumper: ${e.response?.data?.detail || e.message || "network error"}`);
      }
    }
    finally { setLoading(false); }
  };

  const setRow = (i, k, v) => setOnboardRows((r) => r.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addRow = () => setOnboardRows((r) => [...r, { uid: uid(), serial_number: "", size: r[0]?.size || "27.00.49", position: "", tread_depth: "", condition: "Existing" }]);
  const removeRow = (i) => setOnboardRows((r) => r.filter((_, idx) => idx !== i));

  const submitOnboard = async () => {
    if (!siteId) { toast.error("Select your mine site in the Technician Hub first"); return; }
    if (engineHours === "" || engineHours == null) { toast.error("Enter the dumper's current engine hours"); return; }
    const rows = onboardRows.filter((r) => (r.serial_number || "").trim() && r.position);
    if (!rows.length) { toast.error("Add at least one tyre with a serial and position"); return; }
    setSaving(true);
    try {
      await api.post("/onboard-dumper", {
        dumper_number: dumper.trim(), mine_site_id: siteId, engine_hours: Number(engineHours), performed_date: performedDate,
        tyres: rows.map((r) => ({ serial_number: r.serial_number.trim(), size: r.size, position: r.position,
          tread_depth: r.tread_depth === "" ? undefined : Number(r.tread_depth), condition: r.condition || "Existing",
          axles: (meta?.truck_sizes || []).includes(r.size) ? truckAxles : undefined })),
      });
      toast.success(`Added ${rows.length} tyre(s) to ${dumper.trim()}`);
      setNotFound(false);
      await loadTyres();
      setOnboardRows([{ uid: uid(), serial_number: "", size: rows[rows.length - 1]?.size || (meta?.tyre_sizes || [])[1] || "27.00.49", position: "", tread_depth: "", condition: "Existing" }]);
      setAddMore(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add dumper"); }
    finally { setSaving(false); }
  };

  const onDateChange = (d) => { setPerformedDate(d); };

  const setF = (id, k, v) => setFindings((f) => ({ ...f, [id]: { ...f[id], [k]: v } }));

  const submit = async () => {
    if (engineHours === "" || engineHours == null) { toast.error("Enter the current engine hours"); return; }
    if (vehicleKm === "" || vehicleKm == null) { toast.error("Enter the current vehicle KM"); return; }
    const payloadTyres = tyres.map((t) => {
      const f = findings[t.tyre_id] || {};
      return {
        tyre_id: t.tyre_id,
        recommended_action: f.problem ? (f.recommended_action || "Monitor") : "Continue Running",
        overall_condition: f.problem ? (f.overall_condition || "Poor") : "Good",
        problem_types: f.problem ? (f.problem_types || []) : undefined,
        remarks: f.problem ? f.remarks : undefined,
        photos: f.problem ? (f.photos || []) : undefined,
        pressure: f.pressure === "" || f.pressure == null ? undefined : Number(f.pressure),
        tread_depth: f.tread_depth === "" || f.tread_depth == null ? undefined : Number(f.tread_depth),
      };
    });
    setSaving(true);
    try {
      const { data } = await api.post("/inspections/dumper", {
        dumper_number: dumper.trim(), mine_site_id: pickedSite || siteId, size: pickedSize,
        engine_hours: Number(engineHours), vehicle_km: Number(vehicleKm),
        performed_date: performedDate, tyres: payloadTyres,
      });
      toast.success(`Inspected ${data.inspected} tyres on ${data.dumper_number}`);
      onOpenChange(false); reset(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const problemCount = Object.values(findings).filter((f) => f.problem).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="dialog-inspect-dumper">
        <DialogHeader><DialogTitle className="font-display">Inspect Dumper</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Load a dumper to inspect its tyres, or register a new dumper by adding its existing tyres.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Label className="text-xs text-zinc-400">Dumper Number *</Label>
            <div className="mt-1 flex gap-2">
              <Input data-testid="field-dumper_number" value={dumper} onChange={(e) => setDumper(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadTyres()} placeholder="e.g. HAUL-77" className="bg-zinc-950" />
              <Button data-testid="load-dumper-btn" onClick={() => loadTyres()} disabled={loading} variant="outline">
                <MagnifyingGlass size={16} className="mr-1" /> {loading ? "…" : "Load"}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Date it happened</Label>
            <Input data-testid="field-performed_date" type="date" value={performedDate}
              onChange={(e) => onDateChange(e.target.value)} className="mt-1 bg-zinc-950" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Current Engine Hours *</Label>
            <Input data-testid="field-engine_hours" type="number" value={engineHours}
              onChange={(e) => setEngineHours(e.target.value === "" ? "" : Number(e.target.value))} placeholder="required" className="mt-1 bg-zinc-950" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Current Vehicle KM *</Label>
            <Input data-testid="field-vehicle_km" type="number" value={vehicleKm}
              onChange={(e) => setVehicleKm(e.target.value === "" ? "" : Number(e.target.value))} placeholder="required" className="mt-1 bg-zinc-950" />
          </div>
        </div>

        {matches && (
          <div className="mt-2 rounded-sm border border-amber-500/40 bg-amber-500/5 p-3" data-testid="dumper-size-picker">
            <div className="mb-2 text-xs text-amber-400">Two dumpers are numbered "{dumper.trim()}" here — pick the tyre size:</div>
            <div className="flex flex-wrap gap-2">
              {matches.map((m) => (
                <Button key={m.size} variant="outline" data-testid={`pick-size-${m.size}`}
                  onClick={() => loadTyres(m.size, m.mine_site_id)}>{m.site_name ? m.site_name + " · " : ""}{m.size}</Button>
              ))}
            </div>
          </div>
        )}

        {(notFound || addMore) && (
          <div className="mt-2 rounded-sm border border-amber-500/30 bg-amber-500/5 p-3" data-testid="onboard-panel">
            <div className="mb-1 text-sm font-semibold text-amber-400">{notFound ? "New dumper — add its existing tyres" : `Add more tyres to ${dumper.trim()}`}</div>
            <p className="mb-3 text-xs text-zinc-400">{notFound ? "This dumper isn't on record yet. Add its current (old) tyres and their tread, then inspect. Set the dumper's current engine hours above." : "Add another tyre and its tread, then save. You can keep adding as many as you need."}</p>
            {!siteId && <p className="mb-2 text-xs text-rose-400">Select your mine site in the Technician Hub first.</p>}
            {onboardRows.some((r) => (meta?.truck_sizes || []).includes(r.size)) && (
              <div className="mb-3" data-testid="onboard-axles-wrap">
                <Label className="text-xs text-zinc-400">Number of Axles (truck)</Label>
                <Select value={truckAxles} onValueChange={setTruckAxles}>
                  <SelectTrigger className="mt-1 w-56 bg-zinc-950" data-testid="onboard-axles"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 Axles (10 tyres)</SelectItem>
                    <SelectItem value="4">4 Axles (12 tyres)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              {onboardRows.map((r, i) => (
                <div key={r.uid || i} className="grid grid-cols-1 gap-2 sm:grid-cols-12" data-testid={`onboard-row-${i}`}>
                  <Input className="bg-zinc-950 sm:col-span-3" placeholder="Tyre serial" data-testid={`ob-serial-${i}`}
                    value={r.serial_number} onChange={(e) => setRow(i, "serial_number", e.target.value)} />
                  <Select value={r.position} onValueChange={(v) => setRow(i, "position", v)}>
                    <SelectTrigger className="bg-zinc-950 sm:col-span-4" data-testid={`ob-position-${i}`}><SelectValue placeholder="Position" /></SelectTrigger>
                    <SelectContent>{(((meta?.truck_sizes || []).includes(r.size) ? (meta?.positions_by_axles?.[truckAxles] || []) : (meta?.positions_by_size?.[r.size] || meta?.positions || []))).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={r.size} onValueChange={(v) => setRow(i, "size", v)}>
                    <SelectTrigger className="bg-zinc-950 sm:col-span-2"><SelectValue placeholder="Size" /></SelectTrigger>
                    <SelectContent>{(meta?.tyre_sizes || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input className="bg-zinc-950 sm:col-span-2" type="number" placeholder="Tread mm" data-testid={`ob-tread-${i}`}
                    value={r.tread_depth} onChange={(e) => setRow(i, "tread_depth", e.target.value)} />
                  <button type="button" onClick={() => removeRow(i)} className="text-left text-xs text-zinc-500 hover:text-rose-400 sm:col-span-1">✕</button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" onClick={addRow} data-testid="ob-add-row">+ Add tyre</Button>
              <Button size="sm" onClick={submitOnboard} disabled={saving} data-testid="onboard-submit" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
                {saving ? "Adding…" : (notFound ? "Add dumper & load" : "Save tyre(s)")}
              </Button>
              {addMore && !notFound && (
                <Button variant="ghost" size="sm" onClick={() => setAddMore(false)} data-testid="onboard-done">Done adding</Button>
              )}
            </div>
          </div>
        )}

        {tyres && tyres.length > 0 && (
          <div className="mt-2">
            <div className="mb-2 flex items-center justify-between text-xs">
              <span className="overline text-zinc-500">{tyres.length} fitted tyres — mark any with a problem</span>
              <div className="flex items-center gap-3">
                {problemCount > 0 && <span className="text-rose-400">{problemCount} flagged</span>}
                {!addMore && (
                  <Button type="button" variant="outline" size="sm" data-testid="add-more-tyres-btn"
                    onClick={() => { if (!onboardRows.length) setOnboardRows([{ serial_number: "", size: (meta?.tyre_sizes || [])[1] || "27.00.49", position: "", tread_depth: "", condition: "Existing" }]); setAddMore(true); }}>
                    + Add more tyres
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {tyres.map((t) => {
                const f = findings[t.tyre_id] || {};
                return (
                  <div key={t.tyre_id} data-testid={`tyre-finding-${t.tyre_id}`}
                    className={`rounded-sm border p-3 ${f.problem ? "border-rose-500/40 bg-rose-500/5" : "border-zinc-800 bg-zinc-950"}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold">{t.position}</div>
                        <div className="font-mono text-xs text-amber-500">{t.serial_number}
                          <span className="ml-2 text-zinc-500">{t.current_tread_depth != null ? `${t.current_tread_depth}mm` : ""}</span>
                        </div>
                      </div>
                      <button type="button" data-testid={`problem-toggle-${t.tyre_id}`}
                        onClick={() => setF(t.tyre_id, "problem", !f.problem)}
                        className={`flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-xs font-semibold transition-colors ${
                          f.problem ? "border-rose-500 bg-rose-500/15 text-rose-300" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"}`}>
                        {f.problem ? <WarningOctagon size={14} weight="bold" /> : <CheckCircle size={14} weight="bold" />}
                        {f.problem ? "Problem" : "OK"}
                      </button>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-4">
                      <div>
                        <Label className="text-xs text-zinc-400">Pressure (psi)</Label>
                        <Input type="number" data-testid={`pressure-${t.tyre_id}`} value={f.pressure ?? ""}
                          onChange={(e) => setF(t.tyre_id, "pressure", e.target.value)} className="mt-1 w-32 bg-zinc-900" />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-400">Current NSD / Tread (mm)</Label>
                        <Input type="number" step="0.1" data-testid={`tread-${t.tyre_id}`} value={f.tread_depth ?? ""}
                          placeholder={t.current_tread_depth != null ? `was ${t.current_tread_depth}` : ""}
                          onChange={(e) => setF(t.tyre_id, "tread_depth", e.target.value)} className="mt-1 w-40 bg-zinc-900" />
                      </div>
                    </div>

                    {f.problem && (
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <Label className="text-xs text-zinc-400">Problem / Damage Type(s)</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" data-testid={`ptype-trigger-${t.tyre_id}`}
                                className="mt-1 flex w-full items-center justify-between rounded-sm border border-zinc-700 bg-zinc-900 px-3 py-2 text-left text-sm text-zinc-200 hover:border-zinc-500">
                                <span className={(f.problem_types || []).length ? "text-zinc-100" : "text-zinc-500"}>
                                  {(f.problem_types || []).length ? (f.problem_types || []).join(", ") : "Select problem type(s)"}
                                </span>
                                <CaretDown size={14} className="text-zinc-500" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-64 max-h-64 overflow-y-auto p-1">
                              {(meta?.puncture_categories || []).map((pt) => {
                                const sel = (f.problem_types || []).includes(pt);
                                return (
                                  <button type="button" key={pt} data-testid={`ptype-${t.tyre_id}-${pt}`}
                                    onClick={() => { const cur = f.problem_types || []; setF(t.tyre_id, "problem_types", sel ? cur.filter((x) => x !== pt) : [...cur, pt]); }}
                                    className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-zinc-800 ${sel ? "text-rose-300" : "text-zinc-300"}`}>
                                    <span className={`flex h-4 w-4 items-center justify-center rounded-sm border ${sel ? "border-rose-500 bg-rose-500/20" : "border-zinc-600"}`}>
                                      {sel && <CheckCircle size={11} weight="bold" className="text-rose-400" />}
                                    </span>
                                    {pt}
                                  </button>
                                );
                              })}
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <Label className="text-xs text-zinc-400">Recommended Action</Label>
                          <Select value={f.recommended_action || ""} onValueChange={(v) => setF(t.tyre_id, "recommended_action", v)}>
                            <SelectTrigger data-testid={`action-${t.tyre_id}`} className="mt-1 bg-zinc-900"><SelectValue placeholder="Select action" /></SelectTrigger>
                            <SelectContent>{(meta?.inspection_actions || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-zinc-400">Overall Condition</Label>
                          <Select value={f.overall_condition || ""} onValueChange={(v) => setF(t.tyre_id, "overall_condition", v)}>
                            <SelectTrigger className="mt-1 bg-zinc-900"><SelectValue placeholder="Condition" /></SelectTrigger>
                            <SelectContent>{(meta?.conditions || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs text-zinc-400">Notes</Label>
                          <Textarea value={f.remarks || ""} onChange={(e) => setF(t.tyre_id, "remarks", e.target.value)} className="mt-1 bg-zinc-900" />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs text-zinc-400">Photos</Label>
                          <div className="mt-1">
                            <PhotoUpload testid={`photos-${t.tyre_id}`} value={f.photos || []}
                              onChange={(v) => setF(t.tyre_id, "photos", v)} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !tyres || tyres.length === 0} data-testid="dumper-inspect-submit"
            className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
            {saving ? "Saving..." : "Record Inspection"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
